import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input, Label } from "../components/ui/input";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import Footer from "../components/Footer";

function BuildBadge() {
  const [info, setInfo] = useState(null);
  React.useEffect(() => {
    api.get("/build-info").then((r) => setInfo(r.data)).catch(() => {});
  }, []);
  if (!info) return null;
  const built = new Date(info.deploy_timestamp).toLocaleString("en-SG", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
  return (
    <div className="inline-flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-1.5 rounded-sm border border-accent/30 bg-accent/5 font-mono text-xs"
      data-testid="login-build-badge"
      title={`${info.maturity} · ${info.commits} commits · ${info.environment}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-accent inline-block animate-pulse" />
      <span className="text-accent font-semibold" data-testid="login-build-version">{info.version}</span>
      {info.git_sha && info.git_sha !== "unknown" && <span className="text-muted-foreground">{info.git_sha}</span>}
      <span className="text-muted-foreground">· {info.environment} · deployed <span className="text-foreground" data-testid="login-build-date">{built}</span></span>
    </div>
  );
}

export default function Login() {
  const { login, verifyMfa } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [mfaToken, setMfaToken] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(false);

  React.useEffect(() => {
    document.documentElement.classList.add("dark");
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res?.mfa_required) {
        setMfaToken(res.mfa_token);
        setMfaCode("");
        return;
      }
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const onVerifyMfa = async () => {
    if (!mfaCode.trim()) return;
    setLoading(true);
    try {
      await verifyMfa(mfaToken, mfaCode.trim(), rememberDevice);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground dark">
    <div className="flex-1 grid lg:grid-cols-2 min-h-0">
      {/* Left: brand panel */}
      <div className="hidden lg:flex relative bg-[#0F3D3A] overflow-hidden">
        <div
          className="absolute inset-0 opacity-50"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1496939376851-89342e90adcd?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0F3D3A] via-[#0F3D3A]/75 to-[#117A7A]/30" />
        <div className="relative z-10 p-12 flex flex-col justify-between w-full">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 bg-[#F2F4F3] flex items-center justify-center rounded-md p-1.5">
              <img src="/brand/logo_icon.png" alt="Lestari" className="h-full object-contain" />
            </div>
            <div>
              <div className="font-bold tracking-tight text-2xl text-white" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>Lestari</div>
              <div className="text-[9px] uppercase tracking-[0.25em] text-[#D4A84C]">Sustainability · Intelligence · Impact</div>
            </div>
          </div>

          <div className="space-y-6 max-w-md">
            <h1 className="font-display text-5xl font-bold tracking-tighter text-white leading-[1.05]">
              The audit-grade<br/>
              ESG operating system for<br/>
              <span className="text-[#D4A84C]">Singapore enterprises.</span>
            </h1>
            <p className="text-white/70 text-base leading-relaxed">
              Multi-tenant. AI-grounded. Built for IFRS S2, SGX Core Metrics, SEFR and the Carbon Pricing Act.
            </p>
            <div className="grid grid-cols-3 gap-4 pt-6 border-t border-white/10">
              <div>
                <div className="font-mono text-2xl text-accent">10+</div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-white/60">Frameworks</div>
              </div>
              <div>
                <div className="font-mono text-2xl text-accent">4-layer</div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-white/60">Anomaly Engine</div>
              </div>
              <div>
                <div className="font-mono text-2xl text-accent">SG-first</div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-white/60">SEFR · NEA · EMA</div>
              </div>
            </div>
          </div>

          <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">
            Lestari © 2026 — Singapore-residency cloud · SOC 2 target
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-surface">
        <div className="w-full max-w-md space-y-8">
          <div>
            <div className="label-eyebrow mb-3">Sign in</div>
            <h2 className="font-display text-3xl font-bold tracking-tight">Access your control tower.</h2>
            <p className="text-muted-foreground text-sm mt-2">
              Don&apos;t have an account?{" "}
              <Link to="/get-started" className="text-accent hover:underline" data-testid="register-link">
                Start your 15-day trial
              </Link>
            </p>
            <div className="mt-4"><BuildBadge /></div>
          </div>

          {mfaToken ? (
            <div className="space-y-5" data-testid="mfa-panel">
              <div className="flex items-center gap-2 text-accent">
                <ShieldCheck className="h-5 w-5" />
                <span className="font-display font-semibold">Two-factor authentication</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Enter the 6-digit code from your authenticator app (Google Authenticator, Microsoft
                Authenticator, 1Password) — or one of your backup codes.
              </p>
              <div>
                <Label htmlFor="mfa-code">Verification code</Label>
                <Input
                  id="mfa-code"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  autoFocus
                  inputMode="numeric"
                  placeholder="123456 or XXXX-XXXX"
                  data-testid="mfa-code-input"
                  onKeyDown={(e) => e.key === "Enter" && onVerifyMfa()}
                />
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer text-muted-foreground hover:text-foreground">
                <input
                  type="checkbox"
                  checked={rememberDevice}
                  onChange={(e) => setRememberDevice(e.target.checked)}
                  className="accent-[hsl(var(--accent))]"
                  data-testid="mfa-remember-device"
                />
                Remember this device for 30 days
              </label>
              <Button type="button" variant="primary" size="lg" className="w-full" disabled={loading || !mfaCode.trim()} onClick={onVerifyMfa} data-testid="mfa-verify-btn">
                {loading ? "Verifying…" : "Verify & sign in"}
              </Button>
              <button type="button" className="text-xs text-muted-foreground hover:text-foreground underline" onClick={() => { setMfaToken(null); setMfaCode(""); }} data-testid="mfa-back-btn">
                Back to sign in
              </button>
            </div>
          ) : (
          <>
          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email"
                autoComplete="email"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password"
                autoComplete="current-password"
              />
            </div>
            <Button type="submit" variant="primary" size="lg" className="w-full" disabled={loading} data-testid="login-submit-btn">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          </>
          )}
        </div>
      </div>
      </div>
      <Footer />
    </div>
  );
}
